/**
 * Load and interpret place data.
 */
import get from "lodash/get";
import { Place } from "../types";
import debug from "./debug";
import { filterByUniquePropFn, findByPropValueFn } from "./helpers";
import api from "./api";

export let PLACES: Place[] = [];
export const COUNTRY_REGIONS: { [countryIso2: string]: Place[] } = {};

/**
 * Load the place data from a URL (string) or from an array of place objects.
 */
export async function loadPlaces(
  input?: null | string | Place[]
): Promise<Place[]> {
  // Already loaded
  if (PLACES.length > 0) {
    return PLACES;
  }

  debug("[utils/places] loading places...");

  // Load using dataset given
  if (input instanceof Array) {
    debug("[utils/places] Using supplied places data", { input });

    PLACES = input;
  }
  // Load via API
  else {
    debug("[utils/places] Loading the places data from API");

    const receivedPlaces = await api.getPlaces();
    PLACES = receivedPlaces || [];
  }

  debug("[utils/places] loaded places", { PLACES });

  return PLACES;
}

export async function loadCountryRegionsFromPlaces(
  input?: null | string | Place[]
) {
  // Already loaded
  if (Object.keys(COUNTRY_REGIONS).length > 0) {
    return COUNTRY_REGIONS;
  }

  let places: Place[] = input instanceof Array && input.length > 0 ? input : [];
  if (!places.length) {
    places = await loadPlaces(input);
  }

  debug("[utils/places] loading country regions from places...");

  places
    .filter(filterByUniquePropFn("geo.addressCountry"))
    .map(p => get(p, "geo.addressCountry"))
    .reduce((acc, countryCode) => {
      acc[countryCode] = places.filter(
        findByPropValueFn("geo.addressCountry", countryCode)
      );
      return acc;
    }, COUNTRY_REGIONS);

  debug("[utils/places] loaded country regions", { COUNTRY_REGIONS });

  return COUNTRY_REGIONS;
}

/**
 * Get a place's object by its identifier.
 */
export async function getPlaceById(
  identifier: any
): Promise<Place | undefined> {
  const matchedPlaces = await loadPlaces().then(allPlaces =>
    allPlaces.filter(findByPropValueFn("identifier", identifier))
  );

  if (matchedPlaces.length === 1) {
    return matchedPlaces[0];
  }
}
export default PLACES;
